import { LinearProgress } from '@mui/material';
import { grey } from '@mui/material/colors';
import { frFR } from '@mui/material/locale';
import { createTheme, type Theme } from '@mui/material/styles';
import type { Interpolation } from '@mui/system';
import {
  ChevronDown,
  ChevronUp,
  Filter,
  MoreVertical,
  SdxColors,
  SecondaryColors,
  theme as sdxTheme,
} from '@sodexo/ui-components';
import { DataGridNoRowsOverlay } from './Components/DataGrid/DataGridNoRowsOverlay';
import i18n from './i18n';

/**
 * Here we are extending the TypographyPropsVariantOverrides interface to add a new variant called 'subtitle3'.
 * Similarly, we can add other variants for other modules.
 */
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    subtitle3: true;
  }
}

const FilterIcon = () => <Filter style={{ width: 16, height: 16 }} />;
const ChevronUpIcon = () => <ChevronUp style={{ width: 16, height: 16 }} />;
const ChevronDownIcon = () => <ChevronDown style={{ width: 16, height: 16 }} />;
const MoreVerticalIcon = () => <MoreVertical style={{ width: 16, height: 16 }} />;

export const getLocalizationForMui = (language?: string) => {
  if (language === 'fr') {
    return frFR;
  }
  return undefined; // Default to english
};

export const themeVariables = {
  appBarHeight: 67,
  footerHeight: 48,
};

export enum Colors {
  background = '#F9FAFF',
  blue = '#2A295C',
  blue2 = '#3862F8',
  cyan = '#53DCC8',
  green = '#006245',
  lightBlue = '#283897',
  lightBlue2 = '#7E8ACC',
  lighterBlue = '#E7E9F7',
  lightestBlue = '#F9FAFF',
  red = '#EE0000',
  redDark = '#B30000',
  grey = '#65676A',
  lightGrey = '#D3D0C9',
}

const body1Typo = {
  fontSize: 16,
  weight: 400,
  color: Colors.blue,
};

const body2Typo = {
  fontSize: 14,
  weight: 400,
  color: Colors.blue,
};

const subtitle1Typo = {
  fontSize: 14,
  fontWeight: 700,
  color: Colors.blue,
};

const theme = createTheme(sdxTheme, {
  typography: {
    h1: {
      fontSize: 32,
      fontWeight: 600,
      color: Colors.blue,
    },
    h2: {
      fontSize: 28,
      fontWeight: 400,
      color: Colors.blue,
    },
    h3: {
      fontSize: 20,
      fontWeight: 700,
      color: Colors.blue,
    },
    h4: {
      fontSize: 16,
      fontWeight: 700,
      color: Colors.blue,
    },
    h5: {
      ...sdxTheme.typography.h5,
      fontSize: 14,
      fontWeight: 600,
    },
    h6: undefined,
    body1: body1Typo,
    body2: body2Typo,
    subtitle1: subtitle1Typo,
    subtitle2: {
      fontSize: 12,
      fontWeight: 400,
      color: Colors.blue,
    },
    subtitle3: {
      fontSize: 12,
      fontWeight: 700,
      color: Colors.lightBlue,
    },
  },
  palette: {
    primary: {
      main: Colors.blue,
      light: Colors.lightBlue,
      contrastText: '#fff',
    },
    secondary: {
      main: Colors.red,
      contrastText: '#fff',
    },
    text: {
      primary: grey[800],
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          height: themeVariables.appBarHeight,
          borderBottom: `3px solid ${SdxColors.redBright}`,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 600,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <ChevronDown style={{ width: 24, height: 24 }} />,
      },
    },
    MuiPaper: {
      variants: [
        {
          props: { variant: 'sdx' },
          style: {
            padding: 24,
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            borderColor: Colors.lighterBlue,
            borderRadius: 0,
            boxShadow: 'none',
          },
        },
      ],
    },
    MuiCircularProgress: {
      defaultProps: {
        disableShrink: true,
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: SdxColors.blueUltraLight,
        },
        columnHeaderTitle: {
          color: SdxColors.blueDefault,
          fontWeight: 600,
          fontSize: 12,
          textTransform: 'uppercase',
        },
        iconButtonContainer: { display: 'block' },
        columnSeparator: { display: 'none' },
        row: {
          ':hover': {
            backgroundColor: SdxColors.blueUltraLight,
          },
        },
      },
      defaultProps: {
        disableColumnResize: true,
        disableColumnFilter: true,
        disableColumnMenu: true,
        disableColumnSelector: true,
        slots: {
          columnMenuSortAscendingIcon: ChevronUpIcon,
          columnMenuSortDescendingIcon: ChevronDownIcon,
          columnSortedAscendingIcon: ChevronUpIcon,
          columnSortedDescendingIcon: ChevronDownIcon,
          quickFilterIcon: FilterIcon,
          columnMenuFilterIcon: FilterIcon,
          columnFilteredIcon: FilterIcon,
          // columnMenuIcon: MoreVerticalIcon, // Also not working, it seems when using moreActionsIcon + columnsMenuIcon creates a display bug where the icon does not appear unless the column menu is hovered
          moreActionsIcon: MoreVerticalIcon,
          loadingOverlay: () => <LinearProgress />,
          noResultsOverlay: () => <DataGridNoRowsOverlay title={i18n.t('components.dataGrid.noResults')} />,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        valueLabel: {
          backgroundColor: SdxColors.blueDarker,
        },
        markLabel: {
          color: SdxColors.blueDarker,
        },
        mark: {
          display: 'none',
        },
      },
    },
  },
});

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    sdx: true;
  }
}

export default theme;

export const globalStyles: Interpolation<Theme> = {
  ':root': {
    '--toastify-text-color-light': SdxColors.blueDarker,
    '--toastify-font-family': 'Sansa Pro',
    '--toastify-color-info': SdxColors.blueDefault,
    '--toastify-color-success': SdxColors.greenMedium,
    '--toastify-color-warning': SecondaryColors.orange,
    '--toastify-color-error': SdxColors.redBright,
  },
};
